"use client";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  CircleDashed,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { KSeFRegistration, KSeFRegistrationStatus } from "@repo/shared";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InvoiceKSeFStatusProps {
  registration?: KSeFRegistration | null;
  /** Compact mode omits text label, shows icon-only badge */
  compact?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

interface StatusConfig {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: "default" | "secondary" | "destructive" | "outline";
  colorClasses: string;
}

function getStatusConfig(
  registration?: KSeFRegistration | null,
): StatusConfig {
  // No registration at all
  if (!registration) {
    return {
      label: "Not Registered",
      icon: CircleDashed,
      variant: "outline",
      colorClasses: "text-muted-foreground",
    };
  }

  // Registration attempted but failed at the request level
  if (!registration.success) {
    return {
      label: "Registration Failed",
      icon: XCircle,
      variant: "destructive",
      colorClasses: "",
    };
  }

  // Two-stage registration status
  const status: KSeFRegistrationStatus = registration.registrationStatus;

  switch (status) {
    case "Confirmed":
      return {
        label: "Confirmed",
        icon: CheckCircle,
        variant: "default",
        colorClasses:
          "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800",
      };
    case "Failed":
      return {
        label: "KSeF Rejected",
        icon: XCircle,
        variant: "destructive",
        colorClasses: "",
      };
    case "TimedOut":
      return {
        label: "Timed Out",
        icon: AlertTriangle,
        variant: "secondary",
        colorClasses:
          "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800",
      };
    case "Pending":
    default:
      return {
        label: "Pending",
        icon: Clock,
        variant: "secondary",
        colorClasses:
          "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-800",
      };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InvoiceKSeFStatus({
  registration,
  compact = false,
  className,
}: InvoiceKSeFStatusProps) {
  const config = getStatusConfig(registration);
  const Icon = config.icon;

  // Show tooltip with error message for Failed/Registration Failed statuses
  const errorMessage = registration?.errorMessage;
  const showTooltip = !!errorMessage;

  const badge = (
    <Badge
      variant={config.variant}
      className={cn(
        "gap-1",
        config.colorClasses,
        compact && "px-1.5",
        showTooltip && "cursor-help",
        className,
      )}
    >
      <Icon className="size-3" />
      {!compact && <span>{config.label}</span>}
    </Badge>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-sm text-xs font-normal"
        >
          {errorMessage}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Standalone helper to determine what actions are available for a given
 * KSeF registration state. Used by both Dashboard and Invoices pages.
 */
export function getKSeFActions(registration?: KSeFRegistration | null): {
  canRegister: boolean;
  canRetry: boolean;
  canCheckStatus: boolean;
  canRecheckFromKSeF: boolean;
} {
  if (!registration) {
    return { canRegister: true, canRetry: false, canCheckStatus: false, canRecheckFromKSeF: true };
  }

  if (!registration.success) {
    return { canRegister: false, canRetry: true, canCheckStatus: false, canRecheckFromKSeF: true };
  }

  const status = registration.registrationStatus;
  const canCheckStatus = status === "Pending" || status === "TimedOut";
  // Re-check from KSeF: available for Failed or TimedOut registrations that don't have a confirmed KSeF number
  const canRecheckFromKSeF = (status === "Failed" || status === "TimedOut") && !registration.finalKSeFNumber;

  return { canRegister: false, canRetry: false, canCheckStatus, canRecheckFromKSeF };
}
